import { ActivityIndicator, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import theme from '@themes/theme';

interface FilterSheetFooterProps {
  /** Reset only has work to do when something is selected. */
  resetEnabled: boolean;
  /** Apply also accepts an empty draft that differs from what's applied. */
  applyEnabled: boolean;
  onReset: () => void;
  onApply: () => void;
  testIDPrefix: string;
  /** Apply mutation in flight (TA-10495 #6): swaps the "Apply" label for a
   * bare loading spinner — no explanatory text alongside it — and disables
   * both actions until it settles. Omit for sheets with no async apply step. */
  applying?: boolean;
}

/**
 * Reset/Apply footer for the filter sheets and their sub-pages — matches the
 * "CTA Bottom Sheet" component in the Figma spec (node 206:76648 etc., and the
 * Activity / Files & Media frames 188:96487 / 188:112648): plain-text Reset on
 * the left, black pill Apply on the right, both greyed out when there's nothing
 * for them to do.
 *
 * The two are enabled independently. Reset only has work when something is
 * selected, but Apply also has to accept an *empty* draft that differs from what
 * is applied — otherwise clearing the last selected value can never be committed
 * (TA-10334 QA finding 1).
 *
 * Not `CancelDoneFooter` (TA-10224): these sheets' Figma frames spell the
 * actions as Reset (clear the selection, sheet stays open) and Apply (commit),
 * which is different behaviour from Cancel (discard and dismiss) and Done.
 */
export const FilterSheetFooter: React.FC<FilterSheetFooterProps> = ({
  resetEnabled,
  applyEnabled,
  onReset,
  onApply,
  testIDPrefix,
  applying = false,
}) => {
  const insets = useSafeAreaInsets();
  const applyActive = applyEnabled && !applying;

  return (
    <Box>
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        p='m'
        height={80}
        borderTopWidth={1}
        borderTopColor='grey02'>
        <TouchableOpacity
          style={{ flex: 1 }}
          disabled={!resetEnabled || applying}
          testID={`${testIDPrefix}-reset`}
          accessibilityLabel='Reset'
          onPress={onReset}>
          <Box
            paddingHorizontal='m'
            flex={1}
            height={48}
            justifyContent='center'
            alignItems='center'>
            <Text
              variant='labelSmall'
              color={resetEnabled && !applying ? 'black' : 'grey04'}>
              Reset
            </Text>
          </Box>
        </TouchableOpacity>
        <TouchableOpacity
          style={{ flex: 1, marginLeft: theme.spacing.m }}
          testID={`${testIDPrefix}-apply`}
          accessibilityLabel='Apply'
          disabled={!applyActive}
          onPress={onApply}>
          <Box
            height={48}
            flex={1}
            justifyContent='center'
            paddingHorizontal='xl'
            alignItems='center'
            borderRadius='xl'
            backgroundColor={applyActive ? 'black' : 'grey02'}>
            {applying ? (
              <ActivityIndicator color={theme.colors.grey04} />
            ) : (
              <Text
                variant='labelSmall'
                color={applyEnabled ? 'white' : 'grey04'}>
                Apply
              </Text>
            )}
          </Box>
        </TouchableOpacity>
      </Box>
      {/* Safe-area strip below the CTA row. Figma leaves ~42dp under the Apply
          pill on a home-indicator device (node 186:69338), which is the row's
          own 16dp plus this. Living on the footer keeps every step identical —
          the sheet height stays the Figma one either way. */}
      <Box height={insets.bottom} />
    </Box>
  );
};
